import { useQuery } from "@tanstack/react-query";

import { marketFinderSubmissionsService } from "@/features/submissions/services/submissions.service";
import type {
  ConversationInterpretation,
  AccountAnswer,
  MarketQuestion,
  ProposedActionsResponse,
  Relay,
} from "@/features/submissions/types/interpretation.types";

export const interpretationKeys = {
  all: ["conversation-interpretation"] as const,
  application: (appId: number) => ["conversation-interpretation", appId] as const,
  conversation: (appId: number, conversationId: number) =>
    ["conversation-interpretation", appId, conversationId] as const,
};

export const questionKeys = {
  root: ["market-questions"] as const,
  all: (appId: number) => ["market-questions", appId] as const,
};

export const accountAnswerKeys = {
  root: ["market-account-answers"] as const,
  all: (appId: number) => ["market-account-answers", appId] as const,
};

export const proposedActionKeys = {
  root: ["market-proposed-actions"] as const,
  all: (appId: number) => ["market-proposed-actions", appId] as const,
  conversation: (appId: number, conversationId: number) =>
    ["market-proposed-actions", appId, conversationId] as const,
};

export const relayKeys = {
  root: ["market-relays"] as const,
  all: (appId: number) => ["market-relays", appId] as const,
  conversation: (appId: number, conversationId: number) =>
    ["market-relays", appId, conversationId] as const,
};

/** The detailed per-conversation interpretation is drawer-only now — the
 * thread list gets its outcome fields on the list payload itself (BAI-23),
 * so there is no fan-out variant of this query anymore. */
export function useConversationInterpretationQuery(
  appId: number,
  conversationId: number | null,
  enabled = true,
) {
  return useQuery<ConversationInterpretation>({
    queryKey: interpretationKeys.conversation(appId, conversationId ?? -1),
    queryFn: () =>
      marketFinderSubmissionsService.getConversationInterpretation(
        appId,
        conversationId!,
      ),
    enabled: enabled && Number.isFinite(appId) && conversationId != null,
    // These are refreshed by targeted SSE/mutation invalidations. A finite
    // stale window refetches them on every lane remount.
    staleTime: Infinity,
  });
}

/**
 * Every question of the submission, unfiltered. The drawer needs questions
 * from BOTH lanes at once (a market's questions plus the agent answers that
 * resolve them), so one submission-wide fetch replaces per-conversation
 * calls and both lanes read the same cache entry.
 */
export function useQuestionsQuery(appId: number, enabled = true) {
  return useQuery<MarketQuestion[]>({
    queryKey: questionKeys.all(appId),
    queryFn: () => marketFinderSubmissionsService.listQuestions(appId),
    enabled: enabled && Number.isFinite(appId),
    staleTime: Infinity,
  });
}

export function useAccountAnswersQuery(appId: number, enabled = true) {
  return useQuery<AccountAnswer[]>({
    queryKey: accountAnswerKeys.all(appId),
    queryFn: () => marketFinderSubmissionsService.listAccountAnswers(appId),
    enabled: enabled && Number.isFinite(appId),
    staleTime: Infinity,
  });
}

/** BAI-25 proposed actions with their persistent drafts, plus the combined
 * (group) drafts for same-target action groups, for one conversation's
 * inline strips. Actions and groups come pre-computed from the backend's
 * deterministic policy — the UI never composes or re-derives them. */
export function useProposedActionsQuery(
  appId: number,
  conversationId: number | null,
  enabled = true,
) {
  return useQuery<ProposedActionsResponse>({
    queryKey: proposedActionKeys.conversation(appId, conversationId ?? -1),
    queryFn: () =>
      marketFinderSubmissionsService.listProposedActions(appId, conversationId!),
    enabled: enabled && Number.isFinite(appId) && conversationId != null,
    staleTime: Infinity,
  });
}

/** Every proposed action on the submission, in ONE request. The thread list
 * needs to know which conversations have a follow-up waiting, and asking per
 * thread would be a fan-out the list deliberately avoids. */
export function useApplicationProposedActionsQuery(appId: number) {
  return useQuery<ProposedActionsResponse>({
    queryKey: proposedActionKeys.all(appId),
    queryFn: () => marketFinderSubmissionsService.listProposedActions(appId),
    enabled: Number.isFinite(appId),
    staleTime: Infinity,
  });
}

/** Relay provenance touching one conversation — as source OR destination,
 * navigable in both directions (BAI-23). */
export function useRelaysQuery(
  appId: number,
  conversationId: number | null,
  enabled = true,
) {
  return useQuery<Relay[]>({
    queryKey: relayKeys.conversation(appId, conversationId ?? -1),
    queryFn: () =>
      marketFinderSubmissionsService.listRelays(appId, conversationId!),
    enabled: enabled && Number.isFinite(appId) && conversationId != null,
    staleTime: Infinity,
  });
}
